import { codeHostOutcomeMessage, codeHostsKey } from "@/lib/code-host";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "sonner";

// A connect flow comes back with its outcome in the address (?slack=connected,
// ?codehost=..., ...): refresh what it changed, say how it went, and clean the
// address. Used on the Connections home and on each connection's page.
export function useConnectionOutcomes(containerId: string) {
  const queryClient = useQueryClient();
  useEffect(() => {
    const url = new URL(window.location.href);
    const slack = url.searchParams.get("slack");
    const teams = url.searchParams.get("teams");
    const gchat = url.searchParams.get("gchat");
    const discord = url.searchParams.get("discord");
    const codehost = url.searchParams.get("codehost");
    if (slack === "connected") {
      queryClient.invalidateQueries({ queryKey: ["slack", containerId] });
      queryClient.invalidateQueries({ queryKey: ["slack-boo-status"] });
      queryClient.invalidateQueries({ queryKey: ["containers"] });
      queryClient.invalidateQueries({ queryKey: ["secrets", containerId] });
    }
    if (teams === "connected") {
      queryClient.invalidateQueries({ queryKey: ["teams-boo-status"] });
      queryClient.invalidateQueries({ queryKey: ["containers"] });
      queryClient.invalidateQueries({ queryKey: ["secrets", containerId] });
    }
    if (gchat === "connected") {
      queryClient.invalidateQueries({ queryKey: ["gchat-boo-status"] });
      queryClient.invalidateQueries({ queryKey: ["containers"] });
      queryClient.invalidateQueries({ queryKey: ["secrets", containerId] });
    }
    if (discord === "connected") {
      queryClient.invalidateQueries({ queryKey: ["discord-boo-status"] });
      queryClient.invalidateQueries({ queryKey: ["containers"] });
      queryClient.invalidateQueries({ queryKey: ["secrets", containerId] });
    }
    if (codehost) {
      const { kind, text } = codeHostOutcomeMessage(codehost);
      if (kind === "success") toast.success(text);
      else if (kind === "error") toast.error(text);
      else toast(text);
      if (kind !== "error") queryClient.invalidateQueries({ queryKey: codeHostsKey(containerId) });
    }
    // The workspace is already linked to a different Boo workspace — the
    // OAuth callback refused rather than moving it. Surface why.
    if (slack === "workspace_taken") {
      const ws = url.searchParams.get("workspace");
      toast.error(
        `${ws ? `“${ws}”` : "That Slack workspace"} is already connected to another Boo workspace.`,
      );
      url.searchParams.delete("workspace");
    }
    const gchatHistory = url.searchParams.get("gchatHistory");
    if (gchatHistory === "connected") {
      queryClient.invalidateQueries({
        queryKey: ["gchat-custom-user-oauth-status", containerId],
      });
      queryClient.invalidateQueries({
        queryKey: ["gchat-workspace-admin-state", containerId],
      });
      queryClient.invalidateQueries({
        queryKey: ["gchat-workspace-visibility", containerId],
      });
      url.searchParams.delete("gchatHistory");
    }
    if (slack || teams || gchat || discord || codehost) {
      url.searchParams.delete("slack");
      url.searchParams.delete("teams");
      url.searchParams.delete("gchat");
      url.searchParams.delete("discord");
      url.searchParams.delete("codehost");
      url.searchParams.delete("containerId");
      window.history.replaceState(window.history.state, "", url.pathname + (url.search || ""));
    }
  }, [queryClient, containerId]);
}
